import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { formatPence } from '@hcd/domain'
import {
  Archive,
  ArchiveRestore,
  Building2,
  ChevronDown,
  ChevronLeft,
  CircleCheck,
  ClipboardList,
  ExternalLink,
  FileSignature,
  FileText,
  Globe,
  History,
  Inbox,
  Loader2,
  Mail,
  MapPin,
  MoreHorizontal,
  Navigation,
  NotebookPen,
  Pencil,
  Phone,
  Plus,
  Receipt,
  Sparkles,
  UserRound,
  Users,
} from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { AccountChip, CustomerStatusBadge } from '@/components/customers/views'
import { ContactSheet, CustomerEditSheet, SiteSheet } from '@/components/customers/sheets'
import { EnquiryStatusBadge } from '@/components/enquiries/views'
import { EVENT } from '@/components/quotes/quote-panels'
import { StatusBadge } from '@/components/quotes/status-badge'
import { FormField } from '@/components/settings/form-kit'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { api } from '@/lib/api'
import {
  companiesHouseUrl,
  customerProfileQuery,
  INVOICE_FREQUENCY_LABEL,
  mapsSearchUrl,
  PAYMENT_METHOD_LABEL,
  SECTION_LABEL,
  useSaveCustomer,
  websiteHref,
  type CustomerProfile,
  type MissingSection,
} from '@/lib/customers'
import { directionsUrl, telHref } from '@/lib/enquiries'
import { PER, ukDate, type Contact, type QuoteDetail, type Site } from '@/lib/quotes'
import { reportError } from '@/lib/settings'
import { useTitle } from '@/lib/use-title'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app/customers/$id')({ component: CustomerPage })

const CONTRACT_LABEL: Record<string, string> = {
  active: 'Active',
  notice_given: 'Notice given',
  ended: 'Ended',
  cancelled: 'Cancelled',
}

// ---- small building blocks ----------------------------------------------------
function Card({
  id,
  icon,
  title,
  action,
  children,
  className,
  tone,
}: {
  id: string
  icon: ReactNode
  title: string
  action?: ReactNode
  children: ReactNode
  className?: string
  tone?: 'primary'
}) {
  return (
    <section
      aria-labelledby={id}
      className={cn(
        'min-w-0 rounded-xl border bg-card',
        tone === 'primary' && 'border-primary/30',
        className,
      )}
    >
      <header className="flex items-center gap-2 border-b px-5 py-3">
        <span className="text-primary [&_svg]:size-4" aria-hidden>
          {icon}
        </span>
        <h2 id={id} className="min-w-0 flex-1 text-[15px] font-semibold">
          {title}
        </h2>
        {action}
      </header>
      {children}
    </section>
  )
}

/** A label and value. Blank values say why they are blank instead of looking broken. */
function Detail({
  label,
  value,
  fromClient,
  waiting,
  children,
  className,
}: {
  label: string
  value?: string | null
  /** The client normally supplies this when they sign. */
  fromClient?: boolean
  /** The client has not signed and onboarded yet. */
  waiting?: boolean
  children?: ReactNode
  className?: string
}) {
  const empty = !children && !value?.trim()
  return (
    <div className={cn('min-w-0', className)}>
      <dt className="text-[13px] text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-[15px] break-words whitespace-pre-line">
        {!empty ? (
          (children ?? value)
        ) : fromClient && waiting ? (
          <span className="text-muted-foreground italic">Waiting for the client</span>
        ) : (
          <span className="text-muted-foreground">Not added</span>
        )}
      </dd>
    </div>
  )
}

function EditButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <Button variant="ghost" size="sm" className="h-8" onClick={onClick} aria-label={label}>
      <Pencil />
      <span className="max-sm:sr-only">Edit</span>
    </Button>
  )
}

// ---- page -------------------------------------------------------------------------
function CustomerPage() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const { data, isPending, isError, error } = useQuery(customerProfileQuery(id))
  const [editing, setEditing] = useState(false)
  const [contactSheet, setContactSheet] = useState<{ open: boolean; contact: Contact | null }>({
    open: false,
    contact: null,
  })
  const [siteSheet, setSiteSheet] = useState<{ open: boolean; site: Site | null }>({
    open: false,
    site: null,
  })
  const [archiving, setArchiving] = useState(false)
  useTitle(data?.customer.name ?? 'Customer')

  const startQuote = useMutation({
    mutationFn: () =>
      api.post<QuoteDetail>('/quotes', {
        customerId: id,
        contactId: data?.contacts.find((c) => c.isPrimary)?.id ?? data?.contacts[0]?.id ?? null,
        siteId: data?.sites[0]?.id ?? null,
      }),
    onSuccess: (q) => navigate({ to: '/quotes/$id', params: { id: q.id } }),
    onError: (e) => reportError(e),
  })

  if (isError)
    return (
      <p role="alert" className="text-destructive">
        {error.message}
      </p>
    )
  if (isPending) return <Skeleton className="h-96 rounded-xl" />
  const { customer: c, contacts, sites } = data
  const primary = contacts.find((p) => p.isPrimary) ?? contacts[0]
  const phone = primary?.phone || c.mainPhone
  const email = primary?.email || c.mainEmail
  const waiting = !c.onboardingCompletedAt

  return (
    <>
      <Link
        to="/customers"
        className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="size-4" aria-hidden />
        Customers
      </Link>

      <header className="mb-5 grid gap-4 md:flex md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold break-words md:text-[28px]">{c.name}</h1>
            <AccountChip reference={c.reference} />
            <CustomerStatusBadge status={c.status} />
          </div>
          <p className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[15px] text-muted-foreground">
            <span>{c.kind === 'business' ? 'Business' : 'Home owner'}</span>
            {c.sector && <span>{c.sector}</span>}
            {c.accountManagerName && (
              <span className="inline-flex items-center gap-1">
                <UserRound className="size-3.5" aria-hidden />
                Looked after by {c.accountManagerName}
              </span>
            )}
            {c.status === 'archived' && c.archivedReason && <span>{c.archivedReason}</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {phone && (
            <Button variant="outline" className="h-10" render={<a href={telHref(phone)} />}>
              <Phone />
              Call
            </Button>
          )}
          {email && (
            <Button variant="outline" className="h-10" render={<a href={`mailto:${email}`} />}>
              <Mail />
              Email
            </Button>
          )}
          <Button variant="outline" className="h-10" onClick={() => setEditing(true)}>
            <Pencil />
            Edit
          </Button>
          <Button
            className="h-10"
            onClick={() => startQuote.mutate()}
            disabled={startQuote.isPending}
          >
            {startQuote.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
            New quote
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button variant="outline" size="icon" className="size-10" aria-label="More actions">
                  <MoreHorizontal />
                </Button>
              }
            />
            <DropdownMenuContent align="end" className="w-60">
              {c.status === 'archived' ? (
                <RestoreItem profile={data} />
              ) : (
                <DropdownMenuItem onClick={() => setArchiving(true)}>
                  <Archive />
                  Mark as former customer
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <div className="grid min-w-0 content-start gap-4">
          <CompletenessCard profile={data} onEdit={() => setEditing(true)} />
          <AccountsCard profile={data} waiting={waiting} onEdit={() => setEditing(true)} />
          <SitesCard
            profile={data}
            onAdd={() => setSiteSheet({ open: true, site: null })}
            onEdit={(site) => setSiteSheet({ open: true, site })}
          />
          <CompanyCard profile={data} waiting={waiting} onEdit={() => setEditing(true)} />
        </div>
        <div className="grid min-w-0 content-start gap-4">
          <SummaryCard profile={data} />
          <ContactsCard
            contacts={contacts}
            onAdd={() => setContactSheet({ open: true, contact: null })}
            onEdit={(contact) => setContactSheet({ open: true, contact })}
          />
          <NotesCard profile={data} />
          <ActivityCard profile={data} />
        </div>
      </div>

      <CustomerEditSheet profile={data} open={editing} onOpenChange={setEditing} />
      <ContactSheet
        customerId={c.id}
        contact={contactSheet.contact}
        open={contactSheet.open}
        firstContact={contacts.length === 0}
        onOpenChange={(open) => setContactSheet((s) => ({ ...s, open }))}
      />
      <SiteSheet
        customerId={c.id}
        customerName={c.name}
        site={siteSheet.site}
        open={siteSheet.open}
        onOpenChange={(open) => setSiteSheet((s) => ({ ...s, open }))}
      />
      <ArchiveDialog profile={data} open={archiving} onOpenChange={setArchiving} />
    </>
  )
}

// ---- completeness ---------------------------------------------------------------------
function CompletenessCard({ profile, onEdit }: { profile: CustomerProfile; onEdit: () => void }) {
  const { percent, done, total, missing } = profile.completeness
  const [open, setOpen] = useState(percent < 100)
  if (percent >= 100)
    return (
      <p className="flex items-center gap-2 rounded-xl border border-success/30 bg-success/5 px-5 py-3 text-[15px]">
        <CircleCheck className="size-4 text-success" aria-hidden />
        Profile complete
      </p>
    )
  const groups = (Object.keys(SECTION_LABEL) as MissingSection[])
    .map((s) => [s, missing.filter((m) => m.section === s)] as const)
    .filter(([, items]) => items.length > 0)
  const fromClient = missing.filter((m) => m.byClient).length
  return (
    <section aria-labelledby="profile-h" className="rounded-xl border bg-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-5 py-3.5 text-left"
      >
        <Sparkles className="size-4 shrink-0 text-primary" aria-hidden />
        <span className="min-w-0 flex-1">
          <span id="profile-h" className="block text-[15px] font-semibold">
            Profile {done} of {total} filled in
          </span>
          <span className="mt-1.5 block h-2 overflow-hidden rounded-full bg-muted">
            <span
              className="block h-full rounded-full bg-primary"
              style={{ width: `${percent}%` }}
            />
          </span>
        </span>
        <ChevronDown
          className={cn(
            'size-4 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-180',
          )}
          aria-hidden
        />
      </button>
      {open && (
        <div className="grid gap-4 border-t px-5 py-4">
          <p className="text-sm text-muted-foreground">
            Nothing here is required to carry on.
            {fromClient > 0 &&
              ` Items marked "From the client" are filled in by the client when they sign, so there is no need to chase them.`}
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            {groups.map(([section, items]) => (
              <div key={section}>
                <h3 className="text-[13px] font-semibold tracking-wide text-muted-foreground uppercase">
                  {SECTION_LABEL[section]}
                </h3>
                <ul className="mt-1.5 grid gap-1.5">
                  {items.map((m) => (
                    <li key={m.label} className="flex flex-wrap items-center gap-2 text-[15px]">
                      <span
                        className="size-1.5 shrink-0 rounded-full bg-muted-foreground/50"
                        aria-hidden
                      />
                      {m.label}
                      {m.byClient && (
                        <Badge variant="outline" className="font-normal">
                          From the client
                        </Badge>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <Button variant="outline" className="h-10 justify-self-start" onClick={onEdit}>
            <Pencil />
            Fill in what you know
          </Button>
        </div>
      )}
    </section>
  )
}

// ---- accounts ---------------------------------------------------------------------------
function AccountsCard({
  profile,
  waiting,
  onEdit,
}: {
  profile: CustomerProfile
  waiting: boolean
  onEdit: () => void
}) {
  const c = profile.customer
  return (
    <Card
      id="accounts-h"
      icon={<Receipt />}
      title="Accounts and invoicing"
      tone="primary"
      action={<EditButton onClick={onEdit} label="Edit accounts and invoicing" />}
    >
      <dl className="grid gap-4 p-5 sm:grid-cols-2">
        <Detail
          label="Accounts contact"
          value={c.billingContactName}
          fromClient
          waiting={waiting}
        />
        <Detail label="Accounts email" fromClient waiting={waiting} value={c.billingEmail}>
          {c.billingEmail && (
            <a
              className="text-primary underline-offset-4 hover:underline"
              href={`mailto:${c.billingEmail}`}
            >
              {c.billingEmail}
            </a>
          )}
        </Detail>
        <Detail label="Accounts phone" value={c.billingPhone}>
          {c.billingPhone && (
            <a
              className="text-primary underline-offset-4 hover:underline"
              href={telHref(c.billingPhone)}
            >
              {c.billingPhone}
            </a>
          )}
        </Detail>
        <Detail label="Copy invoices to" value={c.billingCcEmail} />
        <Detail
          label="Invoice address"
          value={c.billingAddress}
          fromClient
          waiting={waiting}
          className="sm:col-span-2"
        />
        <Detail
          label="Payment method"
          value={c.paymentMethod ? PAYMENT_METHOD_LABEL[c.paymentMethod] : ''}
        />
        <Detail
          label="Payment terms"
          value={
            c.paymentTermsDays === null
              ? '30 days (company default)'
              : `${c.paymentTermsDays} ${c.paymentTermsDays === 1 ? 'day' : 'days'}`
          }
        />
        <Detail
          label="Invoice frequency"
          value={c.invoiceFrequency ? INVOICE_FREQUENCY_LABEL[c.invoiceFrequency] : ''}
        />
        <Detail
          label="Credit limit"
          value={c.creditLimitPence ? formatPence(c.creditLimitPence) : ''}
        />
        <Detail
          label="Purchase orders"
          value={
            c.poRequired
              ? `Required${c.poReference ? `: ${c.poReference}` : ''}`
              : c.poReference
                ? `PO ${c.poReference}`
                : 'Not needed'
          }
        />
        {c.invoiceNotes && (
          <Detail label="Notes for invoicing" value={c.invoiceNotes} className="sm:col-span-2" />
        )}
        <Detail label="QuickBooks customer ID" value={c.quickbooksCustomerId} />
        <Detail label="SmartTask client ID" value={c.smarttaskClientId} />
      </dl>
      {!c.quickbooksCustomerId && profile.summary.acceptedQuotes > 0 && (
        <p className="border-t px-5 py-3 text-[13px] text-muted-foreground">
          The QuickBooks and SmartTask IDs fill in automatically when the admin set-up tasks on the
          accepted quote are marked done.
        </p>
      )}
    </Card>
  )
}

// ---- sites + map ------------------------------------------------------------------------
function SiteMap({ site, available }: { site: Site; available: boolean }) {
  const [failed, setFailed] = useState(false)
  useEffect(() => setFailed(false), [site.version])
  const place = [site.address, site.town, site.postcode].filter(Boolean).join(', ')
  const canTry = available && (site.postcode || (site.latitude !== null && site.longitude !== null))
  if (!canTry || failed)
    return (
      <div
        className="flex aspect-[16/9] w-full flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed bg-muted/50 px-4 text-center text-sm text-muted-foreground"
        data-testid="map-fallback"
      >
        <MapPin className="size-5" aria-hidden />
        {site.postcode ? 'Map not available right now' : 'Add a postcode to see the map'}
      </div>
    )
  return (
    <a
      href={mapsSearchUrl(site)}
      target="_blank"
      rel="noopener noreferrer"
      className="block overflow-hidden rounded-lg border"
    >
      <img
        src={`/api/v1/sites/${site.id}/map?size=thumb&v=${site.version}`}
        alt={`Map showing ${site.name}${place ? `, ${place}` : ''}`}
        loading="lazy"
        width={640}
        height={360}
        className="aspect-[16/9] w-full bg-muted object-cover"
        onError={() => setFailed(true)}
      />
    </a>
  )
}

function SitesCard({
  profile,
  onAdd,
  onEdit,
}: {
  profile: CustomerProfile
  onAdd: () => void
  onEdit: (s: Site) => void
}) {
  const { sites } = profile
  return (
    <Card
      id="sites-h"
      icon={<MapPin />}
      title={sites.length > 1 ? `Sites (${sites.length})` : 'Site'}
      action={
        <Button variant="ghost" size="sm" className="h-8" onClick={onAdd}>
          <Plus />
          Add site
        </Button>
      }
    >
      {sites.length === 0 ? (
        <p className="p-5 text-[15px] text-muted-foreground">
          No site yet. Add one with its postcode to see it on the map.
        </p>
      ) : (
        <ul className="divide-y">
          {sites.map((s) => {
            const lines = [s.address, s.town, s.county, s.postcode].filter(Boolean)
            return (
              <li key={s.id} className="grid gap-4 p-5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                <SiteMap site={s} available={profile.mapsAvailable} />
                <div className="grid min-w-0 content-start gap-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-semibold break-words">{s.name}</h3>
                      <address className="text-[15px] text-muted-foreground not-italic">
                        {lines.length ? (
                          lines.map((l, i) => (
                            <span key={i} className="block">
                              {l}
                            </span>
                          ))
                        ) : (
                          <span>No address yet</span>
                        )}
                      </address>
                    </div>
                    <EditButton onClick={() => onEdit(s)} label={`Edit ${s.name}`} />
                  </div>
                  {(s.postcode || s.address) && (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9"
                        render={
                          <a href={mapsSearchUrl(s)} target="_blank" rel="noopener noreferrer" />
                        }
                      >
                        <ExternalLink />
                        Open in Google Maps
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9"
                        render={
                          <a href={directionsUrl(s)} target="_blank" rel="noopener noreferrer" />
                        }
                      >
                        <Navigation />
                        Directions
                      </Button>
                    </div>
                  )}
                </div>
                <dl className="grid gap-3 rounded-lg bg-muted/60 p-3 sm:col-span-2 sm:grid-cols-2">
                  <Detail
                    label="Site contact"
                    fromClient
                    waiting={!profile.customer.onboardingCompletedAt}
                    value={[s.siteContactName, s.siteContactPhone].filter(Boolean).join(', ')}
                  >
                    {s.siteContactName && (
                      <>
                        {s.siteContactName}
                        {s.siteContactPhone && (
                          <>
                            {', '}
                            <a
                              className="text-primary underline-offset-4 hover:underline"
                              href={telHref(s.siteContactPhone)}
                            >
                              {s.siteContactPhone}
                            </a>
                          </>
                        )}
                      </>
                    )}
                  </Detail>
                  <Detail
                    label="Preferred start"
                    value={s.preferredStartDate ? ukDate(s.preferredStartDate) : ''}
                  />
                  <Detail label="Opening or access times" value={s.openingHours} />
                  <Detail label="Parking" value={s.parkingNotes} />
                  <Detail
                    label="Access instructions"
                    value={s.accessNotes}
                    className="sm:col-span-2"
                  />
                </dl>
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}

// ---- company -------------------------------------------------------------------------------
function CompanyCard({
  profile,
  waiting,
  onEdit,
}: {
  profile: CustomerProfile
  waiting: boolean
  onEdit: () => void
}) {
  const c = profile.customer
  const business = c.kind === 'business'
  return (
    <Card
      id="company-h"
      icon={<Building2 />}
      title={business ? 'Company' : 'Customer details'}
      action={<EditButton onClick={onEdit} label="Edit company details" />}
    >
      <dl className="grid gap-4 p-5 sm:grid-cols-2">
        {business && (
          <>
            <Detail label="Company number" value={c.companyNumber} fromClient waiting={waiting}>
              {c.companyNumber && (
                <a
                  className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
                  href={companiesHouseUrl(c.companyNumber)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {c.companyNumber}
                  <ExternalLink className="size-3.5" aria-hidden />
                  <span className="sr-only">(opens Companies House)</span>
                </a>
              )}
            </Detail>
            <Detail label="VAT number" value={c.vatNumber} />
          </>
        )}
        <Detail
          label={business ? 'Registered office' : 'Address'}
          value={c.registeredAddress}
          fromClient
          waiting={waiting}
          className="sm:col-span-2"
        />
        {business && (
          <Detail label="What they do" value={c.businessDescription} className="sm:col-span-2" />
        )}
        <Detail label="Main phone" value={c.mainPhone}>
          {c.mainPhone && (
            <a
              className="text-primary underline-offset-4 hover:underline"
              href={telHref(c.mainPhone)}
            >
              {c.mainPhone}
            </a>
          )}
        </Detail>
        <Detail label="Main email" value={c.mainEmail}>
          {c.mainEmail && (
            <a
              className="break-all text-primary underline-offset-4 hover:underline"
              href={`mailto:${c.mainEmail}`}
            >
              {c.mainEmail}
            </a>
          )}
        </Detail>
        <Detail label="Website" value={c.website}>
          {c.website && (
            <a
              className="inline-flex items-center gap-1 break-all text-primary underline-offset-4 hover:underline"
              href={websiteHref(c.website)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Globe className="size-3.5 shrink-0" aria-hidden />
              {c.website.replace(/^https?:\/\//i, '')}
            </a>
          )}
        </Detail>
        <Detail label="Sector" value={c.sector} />
        <Detail label="How they found us" value={c.source} />
        <Detail
          label={profile.summary.firstAcceptedAt ? 'Customer since' : 'Added'}
          value={ukDate(profile.summary.firstAcceptedAt ?? c.createdAt)}
        />
      </dl>
    </Card>
  )
}

// ---- contacts --------------------------------------------------------------------------------
function ContactsCard({
  contacts,
  onAdd,
  onEdit,
}: {
  contacts: Contact[]
  onAdd: () => void
  onEdit: (c: Contact) => void
}) {
  return (
    <Card
      id="contacts-h"
      icon={<Users />}
      title="Contacts"
      action={
        <Button variant="ghost" size="sm" className="h-8" onClick={onAdd}>
          <Plus />
          Add contact
        </Button>
      }
    >
      <ul className="divide-y" aria-label="Contacts">
        {contacts.length === 0 && (
          <li className="p-5 text-[15px] text-muted-foreground">No contacts yet.</li>
        )}
        {contacts.map((p) => (
          <li key={p.id} className="grid gap-1.5 px-5 py-3.5">
            <div className="flex items-start justify-between gap-2">
              <span className="min-w-0 font-medium break-words">
                {p.name}
                {p.jobTitle && (
                  <span className="font-normal text-muted-foreground"> · {p.jobTitle}</span>
                )}
              </span>
              <EditButton onClick={() => onEdit(p)} label={`Edit ${p.name}`} />
            </div>
            {(p.isPrimary || p.isBilling || p.isSite) && (
              <span className="flex flex-wrap gap-1.5">
                {p.isPrimary && <Badge variant="secondary">Main</Badge>}
                {p.isBilling && <Badge variant="secondary">Accounts</Badge>}
                {p.isSite && <Badge variant="secondary">Site</Badge>}
              </span>
            )}
            <span className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {p.email && (
                <a
                  className="inline-flex min-w-0 items-center gap-1.5 break-all hover:text-foreground"
                  href={`mailto:${p.email}`}
                >
                  <Mail className="size-3.5 shrink-0" aria-hidden />
                  {p.email}
                </a>
              )}
              {p.phone && (
                <a
                  className="inline-flex items-center gap-1.5 hover:text-foreground"
                  href={telHref(p.phone)}
                >
                  <Phone className="size-3.5" aria-hidden />
                  {p.phone}
                </a>
              )}
            </span>
            {p.notes && <span className="text-sm text-muted-foreground">{p.notes}</span>}
          </li>
        ))}
      </ul>
    </Card>
  )
}

// ---- commercial summary ------------------------------------------------------------------------
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-lg bg-muted/60 px-3 py-2.5">
      <dt className="text-[13px] text-muted-foreground">{label}</dt>
      <dd className="tabular truncate text-lg font-semibold">{value}</dd>
    </div>
  )
}

function SummaryCard({ profile }: { profile: CustomerProfile }) {
  const { summary, quotes, contracts, enquiries } = profile
  return (
    <Card id="summary-h" icon={<FileText />} title="Work with HCD">
      <dl className="grid grid-cols-2 gap-2 p-5">
        <Stat
          label="Contract value"
          value={
            summary.annualValuePence
              ? `${formatPence(summary.annualValuePence)} a year`
              : 'None yet'
          }
        />
        <Stat label="Live contracts" value={String(summary.liveContracts)} />
        <Stat label="Open quotes" value={String(summary.openQuotes)} />
        <Stat label="Accepted quotes" value={String(summary.acceptedQuotes)} />
      </dl>
      <div className="grid gap-4 border-t p-5">
        <LinkList title="Quotes" icon={<FileText />} empty="No quotes yet.">
          {quotes.map((q) => (
            <li key={q.id}>
              <Link
                to="/quotes/$id"
                params={{ id: q.id }}
                className="-mx-2 flex items-center gap-3 rounded-md px-2 py-2 hover:bg-secondary/60"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">
                    {q.reference}{' '}
                    <span className="font-normal text-muted-foreground">· {q.title}</span>
                  </span>
                  <span className="tabular block truncate text-sm text-muted-foreground">
                    {q.periods
                      .map((p) => `${formatPence(p.grossPence)} ${PER[p.key]}`)
                      .join(' + ') || 'No prices yet'}
                  </span>
                </span>
                <StatusBadge status={q.status} />
              </Link>
            </li>
          ))}
        </LinkList>
        {contracts.length > 0 && (
          <LinkList title="Contracts" icon={<FileSignature />} empty="">
            {contracts.map((k) => (
              <li key={k.id}>
                <Link
                  to="/contracts/$id"
                  params={{ id: k.id }}
                  className="-mx-2 flex items-center gap-3 rounded-md px-2 py-2 hover:bg-secondary/60"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{k.reference}</span>
                    <span className="block truncate text-sm text-muted-foreground">
                      {k.startDate ? `From ${ukDate(k.startDate)}` : k.title}
                      {k.annualValuePence > 0 && ` · ${formatPence(k.annualValuePence)} a year`}
                    </span>
                  </span>
                  <Badge variant={k.status === 'active' ? 'default' : 'outline'}>
                    {CONTRACT_LABEL[k.status] ?? k.status}
                  </Badge>
                </Link>
              </li>
            ))}
          </LinkList>
        )}
        {enquiries.length > 0 && (
          <LinkList title="Enquiries" icon={<Inbox />} empty="">
            {enquiries.map((e) => (
              <li key={e.id}>
                <Link
                  to="/enquiries/$id"
                  params={{ id: e.id }}
                  className="-mx-2 flex items-center gap-3 rounded-md px-2 py-2 hover:bg-secondary/60"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{e.reference}</span>
                    <span className="block truncate text-sm text-muted-foreground">
                      {e.services || `Received ${ukDate(e.createdAt)}`}
                    </span>
                  </span>
                  <EnquiryStatusBadge status={e.status} />
                </Link>
              </li>
            ))}
          </LinkList>
        )}
      </div>
    </Card>
  )
}

function LinkList({
  title,
  icon,
  empty,
  children,
}: {
  title: string
  icon: ReactNode
  empty: string
  children: ReactNode[]
}) {
  return (
    <div>
      <h3 className="flex items-center gap-1.5 text-[13px] font-semibold tracking-wide text-muted-foreground uppercase">
        <span className="[&_svg]:size-3.5" aria-hidden>
          {icon}
        </span>
        {title}
      </h3>
      {children.length === 0 ? (
        <p className="mt-1.5 text-[15px] text-muted-foreground">{empty}</p>
      ) : (
        <ul className="mt-1">{children}</ul>
      )}
    </div>
  )
}

// ---- internal notes --------------------------------------------------------------------------
function NotesCard({ profile }: { profile: CustomerProfile }) {
  const c = profile.customer
  const [text, setText] = useState(c.internalNotes)
  const save = useSaveCustomer(c.id)
  useEffect(() => setText(c.internalNotes), [c.internalNotes])
  const dirty = text !== c.internalNotes
  return (
    <Card id="notes-h" icon={<NotebookPen />} title="Internal notes">
      <div className="grid gap-3 p-5">
        <FormField
          label="Notes for the HCD team"
          hint="Only staff can see these. Never shown to the client."
        >
          {(p) => (
            <Textarea
              {...p}
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="text-base md:text-[15px]"
            />
          )}
        </FormField>
        {dirty && (
          <div className="flex gap-2">
            <Button
              className="h-9"
              disabled={save.isPending}
              onClick={() =>
                save.mutate(
                  { internalNotes: text, expectedVersion: c.version },
                  { onSuccess: () => toast.success('Notes saved') },
                )
              }
            >
              {save.isPending && <Loader2 className="animate-spin" />}
              Save notes
            </Button>
            <Button variant="ghost" className="h-9" onClick={() => setText(c.internalNotes)}>
              Undo
            </Button>
          </div>
        )}
      </div>
    </Card>
  )
}

// ---- activity ------------------------------------------------------------------------------------
function ActivityCard({ profile }: { profile: CustomerProfile }) {
  const items = profile.activity.slice(0, 15)
  return (
    <Card id="activity-h" icon={<History />} title="Recent activity">
      {items.length === 0 ? (
        <p className="p-5 text-[15px] text-muted-foreground">Nothing yet.</p>
      ) : (
        <ol className="grid gap-3 p-5">
          {items.map((e) => {
            const [label, icon] = EVENT[e.type] ?? [
              e.type.replace(/_/g, ' '),
              <ClipboardList key="i" />,
            ]
            return (
              <li key={e.id} className="flex gap-3">
                <span
                  className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-secondary text-secondary-foreground [&_svg]:size-3.5"
                  aria-hidden
                >
                  {icon}
                </span>
                <span className="min-w-0 text-sm">
                  <span className="block font-medium">
                    <Link
                      to="/quotes/$id"
                      params={{ id: e.quoteId }}
                      className="text-primary underline-offset-4 hover:underline"
                    >
                      {e.quoteReference}
                    </Link>{' '}
                    {label}
                  </span>
                  <span className="block text-muted-foreground">
                    {e.actorLabel ? `${e.actorLabel} · ` : ''}
                    {ukDate(e.at, true)}
                  </span>
                </span>
              </li>
            )
          })}
        </ol>
      )}
    </Card>
  )
}

// ---- archive / restore -------------------------------------------------------------------------------
function ArchiveDialog({
  profile,
  open,
  onOpenChange,
}: {
  profile: CustomerProfile
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const c = profile.customer
  const qc = useQueryClient()
  const [reason, setReason] = useState('')
  useEffect(() => {
    if (open) setReason('')
  }, [open])
  const archive = useMutation({
    mutationFn: () =>
      api.post(`/customers/${c.id}/archive`, { reason: reason.trim(), expectedVersion: c.version }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['customer', c.id] })
      void qc.invalidateQueries({ queryKey: ['customers'] })
      toast.success(`${c.name} is now a former customer`)
      onOpenChange(false)
    },
    onError: (e) =>
      reportError(e, () => void qc.invalidateQueries({ queryKey: ['customer', c.id] })),
  })
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Mark {c.name} as a former customer?</DialogTitle>
          <DialogDescription>
            They move out of the main customer list. Their quotes, contracts and history are kept,
            and you can restore them at any time.
          </DialogDescription>
        </DialogHeader>
        <FormField label="Reason (optional)">
          {(p) => (
            <Textarea
              {...p}
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="text-base md:text-[15px]"
            />
          )}
        </FormField>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="h-10" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button className="h-10" onClick={() => archive.mutate()} disabled={archive.isPending}>
            {archive.isPending ? <Loader2 className="animate-spin" /> : <Archive />}
            Mark as former
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function RestoreItem({ profile }: { profile: CustomerProfile }) {
  const c = profile.customer
  const qc = useQueryClient()
  const restore = useMutation({
    mutationFn: () => api.post(`/customers/${c.id}/restore`, { expectedVersion: c.version }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['customer', c.id] })
      void qc.invalidateQueries({ queryKey: ['customers'] })
      toast.success(`${c.name} restored`)
    },
    onError: (e) =>
      reportError(e, () => void qc.invalidateQueries({ queryKey: ['customer', c.id] })),
  })
  return (
    <DropdownMenuItem onClick={() => restore.mutate()} disabled={restore.isPending}>
      <ArchiveRestore />
      Restore customer
    </DropdownMenuItem>
  )
}
